// src/pages/purchase/PurchaseOrderList.js
// Updated: 28 May 2026
// Added: Create Direct PO button (PO_CREATE_ROLES, store added Ravi 2 Nov)
// Added: "Direct" source badge on list items

import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { loadKhaatas } from "../../services/payablesService";
// D-291 P3c: before the GRN a PO shows its ORDERED value (incl. GST) —
// po.grand_total is the RECEIVED value and stays 0 until the GRN.
import { computeOrderValue } from "../../utils/gstBill";
// HIGH-01: who may create / approve / finalize - the shared lists (PO Detail, the service, the rules).
import { PO_APPROVER_ROLES, PO_CREATE_ROLES, SITE_FINALIZE_ROLES, hasPoRole } from "../../utils/poRoles";

const STATUS_UI = {
  draft:         { bg: "#f3f4f6", color: "#374151" },
  pending:       { bg: "#fef3c7", color: "#92400e" },
  approved:      { bg: "#dbeafe", color: "#1e40af" },
  ordered:       { bg: "#ede9fe", color: "#5b21b6" },
  completed:     { bg: "#d1fae5", color: "#065f46" },
  short_closed:  { bg: "#fef3c7", color: "#92400e" },
  short:         { bg: "#fef3c7", color: "#92400e" },
  over:          { bg: "#fee2e2", color: "#991b1b" },
  over_received: { bg: "#fee2e2", color: "#991b1b" },
  cancelled:     { bg: "#f3f4f6", color: "#6b7280" },
};

// Approvers (PO_APPROVER_ROLES) get a "Needs my approval" tab (P3c-6, Ravi 21 Oct).

export default function PurchaseOrderList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [poList,  setPoList]  = useState([]);
  const [filter,  setFilter]  = useState("all"); // all | draft | ordered | completed | direct
  // D-289: { [purchase_order_id]: { amount, settled } } — calculated from the vendor khaata
  const [payByPo, setPayByPo] = useState({});

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const canCreateDirect = hasPoRole(PO_CREATE_ROLES, user.role);
  const isApprover      = hasPoRole(PO_APPROVER_ROLES, user.role);

  const loadPOs = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "purchase_orders"),
        where("company_id", "==", user.company_id || "")
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
      setPoList(data);
      setLoading(false); // show the POs now; the Payment column fills in once the khaata loads

      // D-289 — a PO's payment status is CALCULATED from the vendor khaata
      // (bills settled oldest-first, or the bills the payer chose).
      // po.payment_status is legacy and no longer written. Non-blocking: if
      // this fails, the list still shows, with "Not billed" in Payment.
      try {
        const khaatas = await loadKhaatas(user.company_id || "");
        const map = {};
        for (const k of khaatas) {
          if (k.party_type !== "vendor") continue;
          for (const b of k.bills) {
            if (!b.source_id) continue;
            const m = map[b.source_id] || { amount: 0, settled: 0 };
            m.amount  += b.amount;
            m.settled += b.settled;
            map[b.source_id] = m;
          }
        }
        setPayByPo(map);
      } catch (payErr) {
        console.error("PO payment status (khaata) failed — non-blocking:", payErr);
      }
    } catch (error) {
      console.error("Failed loading PO list", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPOs(); }, []);

  // Filter logic
  const filteredList = poList.filter(po => {
    if (filter === "all")       return true;
    if (filter === "approval")  return (po.po_status || po.status) === "draft";
    if (filter === "direct")    return po.source === "direct";
    if (filter === "draft")     return (po.po_status || po.status) === "draft";
    if (filter === "ordered")   return (po.po_status || po.status) === "ordered";
    if (filter === "completed") return ["completed", "short_closed","short", "over"].includes(po.po_status || po.status);
    return true;
  });

  // Summary counts
  const counts = {
    all:       poList.length,
    approval:  poList.filter(p => (p.po_status || p.status) === "draft").length,
    direct:    poList.filter(p => p.source === "direct").length,
    draft:     poList.filter(p => (p.po_status || p.status) === "draft").length,
    ordered:   poList.filter(p => (p.po_status || p.status) === "ordered").length,
    completed: poList.filter(p => ["completed", "short_closed","short", "over"].includes(p.po_status || p.status)).length,
  };

  return (
    <div className="p-6">

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">Purchase Orders</h1>
          <p className="text-sm text-gray-500 mt-1">Vendor purchase tracking</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => navigate("/purchase-list")}
            className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 transition shadow-sm"
          >
            📋 Purchase Requests
          </button>
          {canCreateDirect && (
            <button
              onClick={() => navigate("/purchase-orders/create")}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Direct PO
            </button>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { key: "all",       label: "All" },
          ...(isApprover ? [{ key: "approval", label: "Needs my approval" }] : []),
          { key: "draft",     label: "Draft" },
          { key: "ordered",   label: "Ordered" },
          { key: "completed", label: "Completed" },
          { key: "direct",    label: "Direct PO" },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition ${
              filter === tab.key
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {tab.label}
            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
              filter === tab.key ? "bg-indigo-500 text-white"
                : tab.key === "approval" && counts.approval > 0 ? "bg-yellow-300 text-yellow-900"
                : "bg-gray-200 text-gray-500"
            }`}>
              {counts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-10 text-center shadow text-gray-500">
          Loading purchase orders...
        </div>
      ) : filteredList.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center shadow">
          <p className="text-gray-500 mb-3">No purchase orders found</p>
          {canCreateDirect && filter !== "direct" && (
            <button
              onClick={() => navigate("/purchase-orders/create")}
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
            >
              + Create Direct PO
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredList.map(po => {
            const status = po.po_status || po.status || "draft";
            const ui = STATUS_UI[status] || STATUS_UI.draft;
            const isDirect = po.source === "direct";

            const awaitingFinalization =
              po.purchase_mode === "direct_site_purchase" &&
              po.site_confirmation_status === "site_confirmed" &&
              hasPoRole(SITE_FINALIZE_ROLES, user.role);

            return (
              <div
                key={po.id}
                onClick={() => navigate(
                  awaitingFinalization
                    ? `/purchase-orders/finalize-site/${po.id}`
                    : `/purchase-orders/${po.id}`
                )}
                className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 cursor-pointer hover:shadow-md transition"
                style={{ borderLeft: `4px solid ${awaitingFinalization ? "#059669" : ui.color}` }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-base font-bold text-slate-800">
                        {po.po_number || "No PO Number"}
                      </h2>
                      {/* Source badge */}
                      {isDirect && (
                        <span className="text-[10px] bg-purple-100 text-purple-700 font-bold px-2 py-0.5 rounded-full">
                          Direct PO
                        </span>
                      )}
                      {isApprover && status === "draft" && (
                        <span className="text-[10px] bg-yellow-100 text-yellow-800 font-bold px-2 py-0.5 rounded-full">
                          ⏳ Needs approval
                        </span>
                      )}
                      {awaitingFinalization && (
                        <span className="text-[10px] bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded-full">
                          ✓ Site Confirmed — Finalize
                        </span>
                      )}
                      {po.grn_locked && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            navigate(`/purchase-orders/${po.id}/grn`);
                          }}
                          className="text-[10px] bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full hover:bg-green-200 transition"
                        >
                          ✅ View GRN
                        </button>
                      )}
                      {po.over_received && (
                        <span className="text-[10px] bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">
                          ⚠ Over Received
                        </span>
                      )}
                      {po.short_closed && (
                        <span className="text-[10px] bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full">
                          Short Closed
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {po.vendor_name || "No Vendor"}
                      {po.created_by_name && (
                        <span className="ml-2 text-slate-400">· {po.created_by_name}</span>
                      )}
                    </p>
                  </div>

                  <div className="text-right shrink-0 ml-3">
                    <span className="text-xs font-bold px-2 py-1 rounded-full capitalize"
                      style={{ background: ui.bg, color: ui.color }}>
                      {/* R8: "short_closed" → "Short closed" (not the raw word) */}
                      {status.replace(/_/g, " ")}
                    </span>
                    <div className="text-xs text-slate-400 mt-1">
                      {po.total_items || po.items?.length || 0} items
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  <div>
                    {po.grn_locked ? (
                      <>
                        <div className="text-xs text-slate-400">Received value</div>
                        <div className="font-bold text-slate-800">
                          ₹{Number(po.grand_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-xs text-slate-400">Order value (incl. GST)</div>
                        <div className="font-bold text-slate-800">
                          ₹{computeOrderValue(po.items || [], null).total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                        </div>
                      </>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Source</div>
                    <div className="text-sm font-semibold text-slate-700 capitalize">
                      {po.source === "direct" ? "Direct" : po.source || "MRF"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Payment</div>
                    <div className="text-sm font-semibold text-slate-700 capitalize">
                      {(() => {
                        const p = payByPo[po.id];
                        if (!p || !(p.amount > 0)) return <span className="text-slate-400">Not billed</span>;
                        const due = Math.round((p.amount - p.settled) * 100) / 100;
                        if (due <= 0)      return <span className="text-green-600">Paid</span>;
                        if (p.settled > 0) return <span className="text-blue-600">Partly · ₹{due.toLocaleString("en-IN")} due</span>;
                        return <span className="text-amber-600">Due</span>;
                      })()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Created</div>
                    <div className="text-sm font-semibold text-slate-700">
                      {po.created_at?.seconds
                        ? new Date(po.created_at.seconds * 1000).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
                        : "—"}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}